import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, type EventSummary } from "@/lib/api";
import { recentEventsStorage, type RecentEvent } from "@/lib/storage";

const LISTENERS = new Set<() => void>();

function emitChange() {
  for (const l of LISTENERS) l();
}

function subscribe(cb: () => void) {
  LISTENERS.add(cb);
  return () => {
    LISTENERS.delete(cb);
  };
}

function getSnapshot() {
  return recentEventsStorage.getAll();
}

/**
 * Gibt die "Meine Veranstaltungen"-Liste aus LocalStorage zurück und hält sie
 * via useSyncExternalStore aktuell (auch wenn andere Tabs im selben Origin
 * schreiben würden — `storage`-Event).
 */
export function useRecentEvents() {
  const entries = useSyncExternalStore(subscribe, getSnapshot, () => []);

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.key === null || e.key === "bm_recent") emitChange();
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }, []);

  const add = useCallback((entry: Omit<RecentEvent, "lastOpened" | "missCount">) => {
    recentEventsStorage.upsert(entry);
    emitChange();
  }, []);

  const remove = useCallback((shortcut: string, role?: "admin" | "user") => {
    recentEventsStorage.remove(shortcut, role);
    emitChange();
  }, []);

  return { entries, add, remove };
}

/**
 * Background-Sync: Einmal beim Mount + alle 5 Minuten werden alle Shortcuts
 * gegen /api/v1/events/sync geschickt. Ergebnis triggert Updates im Storage.
 * Der Render der Karten bleibt unabhängig vom Netzwerk — die Liste rendert
 * sofort aus LocalStorage.
 */
export function useRecentsSync() {
  const { entries } = useRecentEvents();
  const shortcuts = entries.map((e) => e.shortcut);
  const dedupKey = [...new Set(shortcuts)].sort().join(",");

  useQuery({
    queryKey: ["events-sync", dedupKey],
    enabled: shortcuts.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const uniques = [...new Set(shortcuts)];
      const rows = await api.post<EventSummary[]>("/api/v1/events/sync", {
        shortcuts: uniques,
      });
      const map: Record<string, { exists: boolean; name?: string; eventAt?: string }> = {};
      for (const r of rows) {
        map[r.shortcut] = {
          exists: r.exists,
          name: r.name ?? undefined,
          eventAt: r.eventAt ?? undefined,
        };
      }
      recentEventsStorage.applySyncResults(map);
      emitChange();
      return rows;
    },
  });
}
